/**
 * Kant-en-klare stukken document die je met één zin kunt invoegen.
 *
 * <p>Een factuurregel in elkaar zetten kost nu een handvol handelingen die je alleen goed krijgt als je
 * weet hoe de renderer werkt: een herhaalblok met {@code data-repeat="Kostenregel"}, daarbinnen een rij
 * met de juiste klassen, en daarin de tokens. Doe je er één ding naast -- de tokens buiten het
 * herhaalblok, of een rij zonder {@code db-fact-row} -- dan krijg je één regel in plaats van alle
 * regels, of een rij die uit de kolommen loopt. Dat is geen opmaakwerk meer, dat is kennis van het
 * sjabloonformaat.
 *
 * <p><b>Waarom dit geen AI-tekst is.</b> De vraag ("ik wil hier een factuurregel") gaat over een
 * structuur die exact één goede vorm heeft, en die vorm staat al in het meegeleverde factuursjabloon.
 * Een model dat hem elke keer opnieuw verzint, verzint er vroeg of laat een die er goed uitziet en
 * niet werkt -- en dat merk je pas bij de klant op papier. De zin herkennen doen we hier; wat er
 * ingevoegd wordt komt uit vaste bouwstenen.
 */

export interface Bouwblok {
  sleutel: string;
  /** Wat er in de knop staat. */
  naam: string;
  /** Eén regel uitleg onder de naam. */
  uitleg: string;
  /** Woorden die in de vraag mogen staan; alles kleingeletterd vergeleken. */
  trefwoorden: string[];
  /** Welke velden hierin gebruikt worden -- om te kunnen zeggen wat er ontbreekt. */
  velden: string[];
  html: () => string;
}

/** De kolommen van een kostenregel, in de volgorde van het meegeleverde factuursjabloon. */
const KOLOMMEN: Array<{ token: string; kop: string; klasse: string }> = [
  { token: "Kostenregel.Product", kop: "Omschrijving", klasse: "db-fact-oms" },
  { token: "Kostenregel.Aantal", kop: "Aantal", klasse: "db-fact-num" },
  { token: "Kostenregel.Prijs", kop: "Prijs", klasse: "db-fact-num" },
  { token: "Kostenregel.Btw", kop: "BTW", klasse: "db-fact-num" },
  { token: "Kostenregel.Bedrag", kop: "Bedrag", klasse: "db-fact-amt" },
];

/**
 * De kostenregels, met kop en herhaalblok.
 *
 * <p>Alleen de kolommen waarvan het veld in deze installatie bestaat. Een kop "BTW" boven een lege
 * kolom is erger dan geen kolom: op de afdruk lijkt het dan alsof de btw nul is.
 */
function kostenregels(beschikbaar: Set<string>): string {
  const kolommen = KOLOMMEN.filter((k) => beschikbaar.size === 0 || beschikbaar.has(k.token));
  const koppen = kolommen.map((k) => `<span class="${k.klasse}">${k.kop}</span>`).join("");
  const cellen = kolommen.map((k) => `<span class="${k.klasse}">{${k.token}}</span>`).join("");
  return [
    '<div class="db-fact">',
    `<div class="db-fact-row db-fact-head-row">${koppen}</div>`,
    '<div data-repeat="Kostenregel">',
    `<div class="db-fact-row">${cellen}</div>`,
    "</div>",
    "</div>",
  ].join("");
}

/** De totalen onder de regels. Wat er niet bestaat in deze installatie, laten we weg. */
function totalen(beschikbaar: Set<string>): string {
  const regels: Array<{ token: string; label: string; dik?: boolean }> = [
    { token: "Factuur.TotaalExclBtw", label: "Totaal excl. btw" },
    { token: "Factuur.TotaalBtw", label: "Btw" },
    { token: "Factuur.TotaalKorting", label: "Korting" },
    { token: "Factuur.Bedrag", label: "Totaal", dik: true },
    { token: "Factuur.ReedsBetaald", label: "Reeds betaald" },
    { token: "Factuur.TeVoldoen", label: "Te voldoen", dik: true },
  ].filter((r) => beschikbaar.size === 0 || beschikbaar.has(r.token));
  if (regels.length === 0) return "";
  const rijen = regels
    .map(
      (r) =>
        `<div class="db-cost-row${r.dik ? " db-cost-total" : ""}">` +
        `<span class="db-cost-lbl">${r.label}</span>` +
        `<span class="db-cost-amt">{${r.token}}</span></div>`,
    )
    .join("");
  return `<div class="db-costs db-fact-totalen">${rijen}</div>`;
}

export function bouwblokken(beschikbaar: Set<string> = new Set()): Bouwblok[] {
  // Volgorde telt: het meest specifieke blok staat voorop, want de keuze hieronder neemt de eerste
  // treffer. "Factuurregels met totalen" bevat ook het woord "factuurregels", dus bij een score op
  // losse woorden zou het algemene blok altijd winnen.
  return [
    {
      sleutel: "kostenregels-totalen",
      naam: "Factuurregels met totalen",
      uitleg: "De regels, met de totaalregels (excl. btw, btw, te voldoen) eronder.",
      trefwoorden: ["totalen", "totaal", "te voldoen", "optelling", "eindbedrag"],
      velden: [...KOLOMMEN.map((k) => k.token), "Factuur.TeVoldoen"],
      html: () => `${kostenregels(beschikbaar)}${totalen(beschikbaar)}`,
    },
    {
      sleutel: "kostenregels",
      naam: "Factuurregels",
      uitleg: "Een kop met kolommen en een regel die zich herhaalt per kostenregel.",
      trefwoorden: ["factuurregel", "factuurregels", "kostenregel", "kostenregels", "productregel",
        "productregels", "specificatie", "regels", "kosten"],
      velden: KOLOMMEN.map((k) => k.token),
      html: () => kostenregels(beschikbaar),
    },
  ];
}

/**
 * Het bouwblok dat bij een vraag past, of {@code null}.
 *
 * <p>Eerste treffer wint, en de lijst staat van specifiek naar algemeen. Een puntentelling over losse
 * woorden leek slimmer maar koos juist verkeerd: "kostenregels met totalen" bevat drie woorden uit het
 * algemene blok ("kostenregels", "regels", "kosten") en maar één uit het specifieke, dus de totalen
 * vielen weg -- precies waar om gevraagd werd.
 *
 * <p>Bij nul treffers geven we bewust niets terug. Dan hoort het scherm te zeggen wat het wél kan,
 * in plaats van iets in te voegen waar niet om gevraagd is.
 */
export function kiesBouwblok(vraag: string, blokken: Bouwblok[]): Bouwblok | null {
  const tekst = vraag.toLowerCase();
  if (!tekst.trim()) return null;
  return blokken.find((blok) => blok.trefwoorden.some((woord) => tekst.includes(woord))) ?? null;
}
